// JWT session storage shared by the React auth layer and the non-React
// fetch/WebSocket plumbing. The token lives in localStorage so a page
// reload keeps the user signed in until the 7-day JWT expires.
//
// A tiny pub/sub lets the imperative layers (rest.ts on a 401, ws
// reconnect) notify React: when `clearToken()` fires on a 401, the
// AuthProvider subscribes and flips to logged-out, so the route guard
// redirects to /login without a hard reload.

const TOKEN_KEY = 'barter_dashboard_token';

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  listeners.forEach((fn) => fn(token));
}

export function clearToken(): void {
  if (localStorage.getItem(TOKEN_KEY) === null) return;
  localStorage.removeItem(TOKEN_KEY);
  listeners.forEach((fn) => fn(null));
}

/** Subscribe to token changes; returns an unsubscribe fn. */
export function onTokenChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** `Authorization: Bearer` header for the current token, or `{}` if none. */
export function authHeader(): Record<string, string> {
  const token = getToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}
